import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { fmtMoney, fmtDate } from "@/lib/format";
import { Search, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  paid: "bg-[#047857] text-white",
  sent: "bg-[#0A0A0A] text-white",
  overdue: "bg-[#E53E3E] text-white",
  draft: "bg-[#F5F5F5] text-[#666] border border-[#E5E5E5]",
  partially_paid: "bg-[#D97706] text-white",
  void: "bg-[#999] text-white",
};

const StatusBadge = ({ status }) => {
  const s = (status || "").toLowerCase();
  const cls = STATUS_STYLES[s] || "bg-[#F5F5F5] text-[#666] border border-[#E5E5E5]";
  return (
    <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-none ${cls}`}>
      {s.replace("_", " ") || "—"}
    </span>
  );
};

const Invoices = () => {
  const [rows, setRows] = useState([]);
  const [syncedAt, setSyncedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/invoices");
      setRows(r.data.invoices || []);
      setSyncedAt(r.data.synced_at);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (q && !(r.customer_name || "").toLowerCase().includes(q.toLowerCase()) && !(r.invoice_number || "").toLowerCase().includes(q.toLowerCase())) return false;
      if (statusFilter !== "all" && (r.status || "").toLowerCase() !== statusFilter) return false;
      return true;
    });
  }, [rows, q, statusFilter]);

  const totals = useMemo(() => {
    return filtered.reduce(
      (a, r) => ({ total_usd: a.total_usd + (r.total_usd || 0), balance_usd: a.balance_usd + (r.balance_usd || 0) }),
      { total_usd: 0, balance_usd: 0 }
    );
  }, [filtered]);

  const statusCounts = useMemo(() => {
    const c = {};
    rows.forEach((r) => {
      const s = (r.status || "unknown").toLowerCase();
      c[s] = (c[s] || 0) + 1;
    });
    return c;
  }, [rows]);

  return (
    <div className="p-6 md:p-8 max-w-[1600px] mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-[#666] mb-2">
            From Zoho Books
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tighter">Invoices</h1>
          <div className="text-sm font-mono text-[#666] mt-2">
            {syncedAt ? `Last synced: ${fmtDate(syncedAt)}` : "Sync from Dashboard"}
          </div>
        </div>
        <div className="flex gap-3 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
            <input
              data-testid="invoice-search"
              placeholder="Search by customer or #"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="border border-[#E5E5E5] bg-white pl-9 pr-3 py-2 text-sm focus:border-[#0A0A0A] focus:ring-1 focus:ring-[#0A0A0A] outline-none w-64"
            />
          </div>
          <select
            data-testid="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-[#E5E5E5] bg-white px-3 py-2 text-sm focus:border-[#0A0A0A] outline-none"
          >
            <option value="all">All statuses</option>
            {Object.keys(statusCounts).map((s) => (
              <option key={s} value={s}>{s} ({statusCounts[s]})</option>
            ))}
          </select>
          <button
            onClick={load}
            data-testid="refresh-invoices"
            className="border border-[#0A0A0A] px-3 py-2 text-sm font-medium hover:bg-[#0A0A0A] hover:text-white transition-colors flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E5] overflow-x-auto" data-testid="invoices-table">
        <table className="w-full border-collapse min-w-[1000px]">
          <thead>
            <tr className="bg-[#F5F5F5] border-b border-[#1E3A8A]">
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Invoice #</th>
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">PO / Reference</th>
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Customer</th>
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Date</th>
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Due</th>
              <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Status</th>
              <th className="text-right text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Total</th>
              <th className="text-right text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">Balance</th>
              <th className="text-right text-xs font-semibold uppercase tracking-wider text-[#666] py-3 px-4">USD</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={9} className="text-center text-sm text-[#666] py-12">{loading ? "Loading..." : "No invoices"}</td></tr>
            ) : (
              filtered.map((r) => (
                <tr key={r.invoice_id} data-testid={`invoice-row-${r.invoice_id}`} className="border-b border-[#E5E5E5] hover:bg-[#F9F9F9]">
                  <td className="py-3 px-4 text-sm font-mono text-[#0A0A0A] font-medium">{r.invoice_number}</td>
                  <td className="py-3 px-4 text-xs font-mono text-[#1E3A8A]" title={r.reference_number || ""}>
                    {r.reference_number ? (r.reference_number.length > 30 ? r.reference_number.slice(0, 28) + "…" : r.reference_number) : <span className="text-[#CCC]">—</span>}
                  </td>
                  <td className="py-3 px-4 text-sm">{r.customer_name || "—"}</td>
                  <td className="py-3 px-4 text-xs font-mono text-[#666]">{r.date || "—"}</td>
                  <td className="py-3 px-4 text-xs font-mono text-[#666]">{r.due_date || "—"}</td>
                  <td className="py-3 px-4"><StatusBadge status={r.status} /></td>
                  <td className="py-3 px-4 text-sm font-mono text-right">{fmtMoney(r.total, r.currency)}</td>
                  <td className="py-3 px-4 text-sm font-mono text-right text-[#DC2626]">{r.balance > 0 ? fmtMoney(r.balance, r.currency) : "—"}</td>
                  <td className="py-3 px-4 text-sm font-mono text-right">{fmtMoney(r.total_usd, "USD")}</td>
                </tr>
              ))
            )}
          </tbody>
          {filtered.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-[#1E3A8A] bg-[#F5F5F5]">
                <td colSpan={6} className="py-3 px-4 text-xs font-semibold uppercase tracking-wider">Totals ({filtered.length})</td>
                <td></td>
                <td className="py-3 px-4 text-sm font-mono text-right text-[#DC2626] font-semibold">{fmtMoney(totals.balance_usd, "USD")}</td>
                <td className="py-3 px-4 text-sm font-mono text-right font-semibold">{fmtMoney(totals.total_usd, "USD")}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default Invoices;
